import { Component } from '@angular/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth';

interface ErrorMapping {
  [key: string]: string;
}

@Component({
  selector: 'app-forgot-password',
  imports: [
    InputGroupModule, 
    InputGroupAddonModule, 
    FormsModule, 
    InputTextModule, 
    CommonModule, 
    ReactiveFormsModule, 
    ButtonModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './forgot-password.html',
  styleUrl: './forgot-password.scss'
})
export class ForgotPassword {
  loading: boolean = false;
  forgotPasswordForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  private readonly errorMessages: ErrorMapping = {
    'user not found': 'No account found with this email address.',
    'email not found': 'No account found with this email address.',
    'invalid email': 'Please enter a valid email address.',
    'user disabled': 'Your account has been disabled. Please contact support.',
    '0 unknown error': 'Unable to connect to the server. Please check your internet connection and try again.',
    'network error': 'Network error. Please check your internet connection and try again.',
    'timeout': 'Request timed out. Please try again.',
    'bad request': 'Invalid request. Please check your email address and try again.',
  };

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private router: Router
  ) {}

  onSubmit() {
    if (!this.forgotPasswordForm.valid) {
      this.showMessage('warn', 'Validation Error', 'Please enter a valid email address.');
      return;
    }

    this.loading = true;
    const { email } = this.forgotPasswordForm.value;
    const normalizedEmail = email.toLowerCase().trim();
    
    this.authService.requestResetPassword({ email: normalizedEmail }).subscribe({
      next: (response) => this.handleSuccess(response),
      error: (error) => this.handleError(error)
    });
  }

  private handleSuccess(response: any) {
    this.loading = false;
    this.showMessage('success', 'Success', response.message || 'Password reset link has been sent to your email.');
    
    setTimeout(() => {
      this.router.navigate(['/']);
    }, 2000);
  }

  private handleError(error: any) {
    this.loading = false;
    const errorMessage = this.extractErrorMessage(error);
    this.showMessage('error', 'Error', errorMessage);
  }

  private extractErrorMessage(error: any): string {
    const message = error.error?.message || error.message || 'Failed to send password reset link. Please try again.';
    const lowerMessage = message.toLowerCase();
    
    // Handle specific HTTP status codes
    if (error.status === 0 || lowerMessage.includes('0 unknown error')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (error.status === 400) {
      // Handle 400 Bad Request specifically
      if (lowerMessage.includes('email') || lowerMessage.includes('user')) {
        return 'No account found with this email address. Please check your email and try again.';
      }
      return 'Invalid request. Please check your email address and try again.';
    }
    
    
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    
    // Check for specific error patterns in the message
    for (const [pattern, userMessage] of Object.entries(this.errorMessages)) {
      if (lowerMessage.includes(pattern)) {
        return userMessage;
      }
    }
    
    return 'Failed to send password reset link. Please try again.';
  }

  private showMessage(severity: string, summary: string, detail: string) {
    this.messageService.add({ severity, summary, detail });
  }

  onBackToLogin() {
    this.router.navigate(['/']);
  }
}
